/**
 * Hooks para CRUD de plantillas email + preview.
 *
 * Patrón: usePdfTemplates pero con 3 sub-templates (subject + html + text).
 * Mutations toast-feedback con sonner.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  emailTemplatesApi,
  type DocType,
  type EmailTemplate,
  type ListResponse,
} from '@/lib/api-email-templates'

const queryKeys = {
  list: (clientId: string) => ['admin', 'email-templates', clientId] as const,
  get: (clientId: string, docType: DocType) =>
    ['admin', 'email-templates', clientId, docType] as const,
  default: (docType: DocType) =>
    ['admin', 'email-templates', 'default', docType] as const,
}

export function useEmailTemplates(clientId: string | undefined) {
  return useQuery<ListResponse<EmailTemplate>>({
    queryKey: queryKeys.list(clientId ?? ''),
    queryFn: () => emailTemplatesApi.list(clientId!),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  })
}

export function useEmailTemplate(clientId: string | undefined, docType: DocType) {
  return useQuery<EmailTemplate>({
    queryKey: queryKeys.get(clientId ?? '', docType),
    queryFn: () => emailTemplatesApi.get(clientId!, docType),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  })
}

export function useDefaultEmailTemplate(docType: DocType) {
  return useQuery<EmailTemplate>({
    queryKey: queryKeys.default(docType),
    queryFn: () => emailTemplatesApi.getDefault(docType),
    staleTime: Infinity,
  })
}

interface UpsertVars {
  clientId: string
  docType: DocType
  subject_template: string
  html_template: string
  text_template: string
}

export function useUpsertEmailTemplate() {
  const qc = useQueryClient()
  return useMutation<EmailTemplate, Error, UpsertVars>({
    mutationFn: ({ clientId, docType, ...body }) =>
      emailTemplatesApi.upsert(clientId, docType, body),
    onSuccess: (_data, { clientId, docType }) => {
      qc.invalidateQueries({ queryKey: queryKeys.list(clientId) })
      qc.invalidateQueries({ queryKey: queryKeys.get(clientId, docType) })
      toast.success('Plantilla email guardada')
    },
    onError: (err) => {
      toast.error(`No se pudo guardar: ${extractMessage(err)}`)
    },
  })
}

interface DeleteVars {
  clientId: string
  docType: DocType
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient()
  return useMutation<void, Error, DeleteVars>({
    mutationFn: ({ clientId, docType }) =>
      emailTemplatesApi.delete(clientId, docType),
    onSuccess: (_data, { clientId, docType }) => {
      qc.invalidateQueries({ queryKey: queryKeys.list(clientId) })
      qc.invalidateQueries({ queryKey: queryKeys.get(clientId, docType) })
      toast.success('Plantilla eliminada — vuelve al default Taxor')
    },
    onError: (err) => {
      toast.error(`No se pudo eliminar: ${extractMessage(err)}`)
    },
  })
}

function extractMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'error desconocido'
}
